/**
 * Payment TanStack queries — balance, payment sources, auto-reload.
 */

import { useQuery } from "@tanstack/react-query";
import { analyticsKeys, paymentKeys, offerKeys } from "@/queries/keys";
import * as paymentApi from "@/features/subscription/api";
import { apiClient } from "@/lib/api/client";
import { queryClient } from "@/providers/query-provider";
import type { PaymentMethod, PaymentSourceType } from "@/features/subscription/types";

/**
 * Refetch every saved-payment-methods list (Stripe + Paddle), active AND inactive,
 * so profile, subscription, and add-balance all reflect an add/remove — not just the
 * currently-mounted list. Uses the singleton client so non-React callers (paddle.ts)
 * can trigger it too.
 */
export function refreshPaymentSources(): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: [...paymentKeys.all, "sources"],
    refetchType: "all",
  });
}

// Server returns three shapes — Stripe card (card.*), Stripe Link (type="link"
// + email), Paddle PayPal (type="paddle" + top-level email, no card subfield).
// Flattened to the client PaymentMethod shape below.
interface RawStripePaymentMethod {
  id: string;
  type?: string;
  card?: {
    brand?: string;
    display_brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
  };
  link?: { email?: string };
  billing_details?: { email?: string };
  email?: string;
  isDefault?: boolean;
  metadata?: { isDefault?: string };
}

function normalizePaymentMethod(
  raw: RawStripePaymentMethod,
  paymentSource: PaymentSourceType,
): PaymentMethod {
  const isDefault = raw.isDefault ?? raw.metadata?.isDefault === "true";

  // paymentSource=paddle returns { id, email } with no type field — set it explicitly.
  const type: PaymentMethod["type"] =
    paymentSource === ("paddle" as PaymentSourceType)
      ? "paddle"
      : raw.type === "link"
        ? "link"
        : "card";

  if (type === "card") {
    const card = raw.card ?? {};
    return {
      id: raw.id,
      type,
      isDefault,
      brand: card.display_brand ?? card.brand ?? "card",  // machine string; UI maps to label
      last4: card.last4 ?? "••••",
      expMonth: card.exp_month ?? 0,
      expYear: card.exp_year ?? 0,
    };
  }

  if (type === "link") {
    return {
      id: raw.id,
      type,
      isDefault,
      email: raw.link?.email ?? raw.billing_details?.email ?? raw.email,
    };
  }

  // type === "paddle"
  return { id: raw.id, type, isDefault, email: raw.email };
}

export interface TaxInfo {
  taxType: string;
  taxNumber: string;
  description: string;
}

export interface BillingAddress {
  id: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  isDefault: boolean;
}

// Server stores billing addresses as a nested shape; flatten to the
// BillingAddress shape consumed by the UI.
interface RawBillingAddress {
  _id?: string;
  firstName?: string;
  lastName?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    country?: string;
    zipcode?: string;
    isDefault?: boolean;
  };
}

function normalizeBillingAddress(raw: RawBillingAddress): BillingAddress {
  const addr = raw.address ?? {};
  return {
    id: raw._id ?? "",
    firstName: raw.firstName ?? "",
    lastName: raw.lastName ?? "",
    address1: addr.line1 ?? "",
    address2: addr.line2 ?? "",
    city: addr.city ?? "",
    state: addr.state ?? "",
    zipCode: addr.zipcode ?? "",
    country: addr.country ?? "",
    isDefault: addr.isDefault ?? false,
  };
}

/** Server payload shape — matches the validation schema on `/user/address`. */
export interface BillingAddressPayload {
  firstName: string;
  lastName: string;
  address1?: string;
  address2?: string | null;
  city?: string;
  state?: string | null;
  country?: string;
  zipCode?: string;
  isDefault?: boolean;
}

/**
 * Fetch current account balance.
 */
export function useBalance() {
  return useQuery({
    queryKey: paymentKeys.balance(),
    queryFn: async () => {
      const res = await paymentApi.getBalance();
      if (!res.success) throw new Error(res.error ?? "Failed to fetch credit");
      return res.data!;
    },
    // 30s freshness covers cross-tab balance race during cost-review without refetch storms on Stripe/Paddle window-focus return.
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch saved payment methods (cards / Paddle sources).
 */
export function usePaymentMethods(
  paymentSource: PaymentSourceType = "stripe" as PaymentSourceType
) {
  return useQuery({
    queryKey: paymentKeys.sources(paymentSource),
    queryFn: async () => {
      const res = await paymentApi.listSources(paymentSource);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch payment methods");
      const raw = (res.data ?? []) as unknown as RawStripePaymentMethod[];
      const normalized = raw.map((r) => normalizePaymentMethod(r, paymentSource));
      return normalized;
    },
    staleTime: 5 * 60 * 1000,
    // Pair with staleTime to prevent thrash on Stripe 3DS / Paddle popup window-focus return.
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch auto-reload configuration.
 */
export function useAutoReload() {
  return useQuery({
    queryKey: paymentKeys.autoReload(),
    queryFn: async () => {
      const res = await paymentApi.getAutoReloadStatus();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch auto-reload config");
      return res.data!;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch usage analytics for the current account.
 */
export function useUsageAnalytics() {
  return useQuery({
    queryKey: analyticsKeys.usage(),
    queryFn: async () => {
      const res = await paymentApi.getUsageAnalytics();
      if (!res.success) throw new Error(res.error ?? "Failed to fetch usage analytics");
      return res.data!;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch available offers for the current user.
 */
export function useOffers() {
  return useQuery({
    queryKey: offerKeys.all,
    queryFn: async () => {
      const res = await paymentApi.getOffers();
      if (!res.success) throw new Error(res.error ?? "Failed to fetch offers");
      return res.data!;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch special offers (isSpecialOffer) for the current user.
 */
export function useSpecialOffers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: offerKeys.special(),
    queryFn: async () => {
      const res = await paymentApi.getSpecialOffers();
      if (!res.success) throw new Error(res.error ?? "Failed to fetch offers");
      return res.data!;
    },
    enabled: options?.enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch eligible offers for a signed link token (public /claim-offer route —
 * no login). Disabled when `token` is empty.
 */
export function usePublicOffers(token: string) {
  return useQuery({
    queryKey: offerKeys.byToken(token),
    queryFn: async () => {
      const res = await paymentApi.getOffersByToken(token);
      if (!res.success) throw new Error(res.error ?? "Failed to fetch offers");
      return res.data!;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTaxInfo() {
  return useQuery({
    queryKey: paymentKeys.taxInfo(),
    queryFn: async () => {
      const res = await apiClient.get<TaxInfo>("/user/tax");
      if (!res.success) throw new Error(res.error ?? "Failed to fetch tax information");
      return res.data ?? { taxType: "none", taxNumber: "", description: "" };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useBillingAddresses() {
  return useQuery({
    queryKey: paymentKeys.billingAddresses(),
    queryFn: async () => {
      const res = await apiClient.get<RawBillingAddress[]>("/user/address");
      if (!res.success) throw new Error(res.error ?? "Failed to fetch billing addresses");
      return (res.data ?? []).map(normalizeBillingAddress);
    },
    staleTime: 5 * 60 * 1000,
  });
}
